import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ADMIN_PROJECTS,
  yearKey,
  quarterKey,
  monthKey,
  monthName,
  daysInMonth,
  dayKey,
  todayStr,
} from "@/lib/adminApi";
import ItemList from "@/components/admin/ItemList";
import Heatmap from "@/components/admin/Heatmap";
import DayDrawer from "@/components/admin/DayDrawer";
import RecurringPanel from "@/components/admin/RecurringPanel";
import AIRecapButton from "@/components/admin/AIRecapButton";

/**
 * AdminPlanning — hierarchical planning view for one project.
 * Levels: year → quarter → month → day(drawer)
 */
export default function AdminPlanning() {
  const { slug: routeSlug } = useParams();
  const nav = useNavigate();
  const validSlug = ADMIN_PROJECTS.find((p) => p.slug === routeSlug)?.slug;

  const initialSlug = validSlug || ADMIN_PROJECTS[0].slug;
  const initialYear = new Date().getFullYear();
  const initialQ = Math.ceil((new Date().getMonth() + 1) / 3);

  const [slug, setSlug] = useState(initialSlug);
  const [year, setYear] = useState(initialYear);
  const [level, setLevel] = useState("year"); // year | quarter | month
  const [q, setQ] = useState(initialQ);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [drawerDate, setDrawerDate] = useState(null);

  // sync URL when slug changes
  useEffect(() => {
    if (slug !== routeSlug) {
      nav(`/admin/planning/${slug}`, { replace: true });
    }
  }, [slug]);

  const onSlugChange = (newSlug) => {
    setSlug(newSlug);
    setLevel("year");
  };

  return (
    <div className="space-y-10" data-testid="admin-planning">
      {/* Project tabs */}
      <div className="flex flex-wrap gap-2">
        {ADMIN_PROJECTS.map((p) => (
          <button
            key={p.slug}
            onClick={() => onSlugChange(p.slug)}
            className={`px-3 py-2 text-xs tracking-widest border transition-colors ${
              p.slug === slug
                ? "border-four-yellow text-four-yellow"
                : "border-parchment-faint opacity-60 hover:opacity-100 hover:border-four-yellow"
            }`}
            data-testid={`planning-project-${p.slug}`}
          >
            {p.name.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Year selector + breadcrumb */}
      <div className="flex items-center justify-between border-b border-parchment-faint pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setYear((y) => y - 1)}
            className="opacity-60 hover:opacity-100"
            data-testid="year-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className="font-serif text-4xl" data-testid="year-label">
            {year}
          </h1>
          <button
            onClick={() => setYear((y) => y + 1)}
            className="opacity-60 hover:opacity-100"
            data-testid="year-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <nav className="text-xs tracking-widest opacity-60 flex items-center gap-2">
          <button
            onClick={() => setLevel("year")}
            className={level === "year" ? "text-four-yellow" : "hover:text-four-yellow"}
            data-testid="bc-year"
          >
            YEAR
          </button>
          {level !== "year" && <span>/</span>}
          {level !== "year" && (
            <button
              onClick={() => setLevel("quarter")}
              className={level === "quarter" ? "text-four-yellow" : "hover:text-four-yellow"}
              data-testid="bc-quarter"
            >
              Q{q}
            </button>
          )}
          {level === "month" && (
            <>
              <span>/</span>
              <span className="text-four-yellow">{monthName(month).toUpperCase()}</span>
            </>
          )}
        </nav>
      </div>

      {/* ----- YEAR LEVEL ----- */}
      {level === "year" && (
        <div className="space-y-10">
          <section className="border border-parchment-faint p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs tracking-widest opacity-60 text-four-yellow">YEAR GOALS</p>
              <AIRecapButton
                project_slug={slug}
                period="year"
                anchor_date={dayKey(year, 6, 30)}
              />
            </div>
            <ItemList
              kind="goal"
              project_slug={slug}
              scope="year"
              scope_key={yearKey(year)}
              title=""
            />
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((qq) => (
              <button
                key={qq}
                onClick={() => {
                  setQ(qq);
                  setLevel("quarter");
                }}
                className="border border-parchment-faint p-6 text-left hover:border-four-yellow transition-colors"
                data-testid={`q-card-${qq}`}
              >
                <p className="text-xs tracking-widest opacity-60 mb-2">Q{qq}</p>
                <p className="font-serif text-xl">
                  {monthName(qq * 3 - 2)} – {monthName(qq * 3)}
                </p>
              </button>
            ))}
          </div>

          <section>
            <p className="text-xs tracking-widest opacity-60 mb-4 text-four-yellow">
              ACTIVITY HEAT-MAP
            </p>
            <Heatmap
              project_slug={slug}
              year={year}
              onDayClick={(d) => setDrawerDate(d)}
            />
          </section>

          <RecurringPanel project_slug={slug} />
        </div>
      )}

      {/* ----- QUARTER LEVEL ----- */}
      {level === "quarter" && (
        <div className="space-y-10">
          <section className="border border-parchment-faint p-6">
            <p className="text-xs tracking-widest opacity-60 text-four-yellow mb-4">
              Q{q} {year} · GOALS
            </p>
            <ItemList
              kind="goal"
              project_slug={slug}
              scope="quarter"
              scope_key={quarterKey(year, q)}
              title=""
            />
          </section>

          <section className="border border-parchment-faint p-6">
            <p className="text-xs tracking-widest opacity-60 text-four-yellow mb-4">
              Q{q} {year} · TASKS
            </p>
            <ItemList
              kind="task"
              project_slug={slug}
              scope="quarter"
              scope_key={quarterKey(year, q)}
              title=""
            />
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[q * 3 - 2, q * 3 - 1, q * 3].map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMonth(m);
                  setLevel("month");
                }}
                className="border border-parchment-faint p-6 text-left hover:border-four-yellow transition-colors"
                data-testid={`month-card-${m}`}
              >
                <p className="text-xs tracking-widest opacity-60 mb-2">MONTH</p>
                <p className="font-serif text-xl">{monthName(m)}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ----- MONTH LEVEL ----- */}
      {level === "month" && (
        <div className="space-y-10">
          <section className="border border-parchment-faint p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs tracking-widest opacity-60 text-four-yellow">
                {monthName(month).toUpperCase()} {year} · GOALS
              </p>
              <AIRecapButton
                project_slug={slug}
                period="month"
                anchor_date={dayKey(year, month, 15)}
              />
            </div>
            <ItemList
              kind="goal"
              project_slug={slug}
              scope="month"
              scope_key={monthKey(year, month)}
              title=""
            />
          </section>

          <section className="border border-parchment-faint p-6">
            <p className="text-xs tracking-widest opacity-60 text-four-yellow mb-4">
              {monthName(month).toUpperCase()} {year} · TASKS
            </p>
            <ItemList
              kind="task"
              project_slug={slug}
              scope="month"
              scope_key={monthKey(year, month)}
              title=""
            />
          </section>

          <section>
            <p className="text-xs tracking-widest opacity-60 mb-4 text-four-yellow">
              DAYS — click any day to add tasks &amp; diary
            </p>
            <MonthCalendar
              year={year}
              month={month}
              project_slug={slug}
              onDayClick={(d) => setDrawerDate(d)}
            />
          </section>
        </div>
      )}

      <DayDrawer
        project_slug={slug}
        date={drawerDate}
        open={!!drawerDate}
        onClose={() => setDrawerDate(null)}
      />
    </div>
  );
}

function MonthCalendar({ year, month, project_slug, onDayClick }) {
  const n = daysInMonth(year, month);
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const cells = Array.from({ length: firstDay + n }, (_, i) =>
    i < firstDay ? null : i - firstDay + 1
  );
  const today = todayStr();

  return (
    <div data-testid="month-calendar">
      <div className="grid grid-cols-7 gap-1 mb-2 text-[10px] tracking-widest opacity-50">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-center py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null)
            return <div key={i} className="aspect-square" />;
          const key = dayKey(year, month, d);
          const isToday = key === today;
          return (
            <button
              key={i}
              onClick={() => onDayClick(key)}
              className={`aspect-square border ${
                isToday
                  ? "border-four-yellow text-four-yellow"
                  : "border-parchment-faint/40"
              } hover:border-four-yellow text-sm transition-colors`}
              data-testid={`cal-day-${key}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
